// src/components/admin/testSeries/MainsTestUpload.jsx
import React, { useState } from "react";
import axios from "axios";

// ✅ Centralize API base (works for dev + production)
const API_BASE =
  process.env.NODE_ENV === "production"
    ? "https://upsc-backend-353e.onrender.com" // ⚠️ replace with your backend domain (e.g., https://api.yourdomain.com)
    : "http://localhost:5000";

const MainsTestUpload = () => {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [section, setSection] = useState("");
  const [subject, setSubject] = useState("");
  const [questions, setQuestions] = useState([{ question: "", modelAnswer: "" }]);
  const [status, setStatus] = useState("draft");
  const [isFree, setIsFree] = useState(true); // 🔥 NEW
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const handleQuestionChange = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;
    setQuestions(updated);
  };

  const addQuestion = () =>
    setQuestions([...questions, { question: "", modelAnswer: "" }]);

  const removeQuestion = (index) =>
    setQuestions(questions.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    // ✅ Validation
    if (!title || !date || !section || !subject) {
      setMessage("❌ All fields are required.");
      setLoading(false);
      return;
    }

    if (questions.length === 0 || questions.some((q) => !q.question.trim())) {
      setMessage("❌ Each question must have text.");
      setLoading(false);
      return;
    }

    const parsedDate = new Date(date);
    if (isNaN(parsedDate)) {
      setMessage("❌ Invalid date format.");
      setLoading(false);
      return;
    }

    const payload = {
  title: title.trim(),
  date: parsedDate,
  section,
  subject,
  status,
  isFree, // 🔥 ADD THIS LINE
  questions: questions.map((q) => ({
        question: q.question.trim(),
        modelAnswer: q.modelAnswer?.trim() || "",
      })),
    };

    try {
      const token = JSON.parse(localStorage.getItem("user"))?.token;

      if (!token) {
        setMessage("❌ Authentication error. Please log in again.");
        setLoading(false);
        return;
      }

      // ✅ Use API_BASE here
      const res = await axios.post(
        `${API_BASE}/api/admin/test-series/mains`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("✅ Upload Response:", res.data);
      setMessage("✅ Mains Test uploaded successfully.");

      // Reset form
      setTitle("");
      setDate("");
      setSection("");
      setSubject("");
      setStatus("draft");
      setIsFree(true); // 🔥 RESET
      setQuestions([{ question: "", modelAnswer: "" }]);
    } catch (err) {
      console.error("❌ Upload Error:", err.response?.data || err.message);
      setMessage(
        `❌ Error uploading test: ${err.response?.data?.message || err.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white dark:bg-gray-900 shadow rounded-lg mt-6">
      <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
        Upload Mains Test
      </h2>

      {message && (
        <div
          className={`mb-4 p-2 rounded text-sm ${
            message.startsWith("✅")
              ? "text-green-600 bg-green-50 dark:text-green-400 dark:bg-gray-800"
              : "text-red-600 bg-red-50 dark:text-red-400 dark:bg-gray-800"
          }`}
        >
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Title */}
        <div className="mb-4">
          <label className="block mb-1 font-medium text-gray-800 dark:text-gray-200">
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            className="w-full p-2 border rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
          />
        </div>

        {/* Date */}
        <div className="mb-4">
          <label className="block mb-1 font-medium text-gray-800 dark:text-gray-200">
            Date
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            className="w-full p-2 border rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
          />
        </div>

        {/* Section */}
        <div className="mb-4">
          <label className="block mb-1 font-medium text-gray-800 dark:text-gray-200">
            Section
          </label>
          <select
            value={section}
            onChange={(e) => setSection(e.target.value)}
            required
            className="w-full p-2 border rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
          >
            <option value="">-- Select Section --</option>
            <option value="GS-1">GS-1</option>
            <option value="GS-2">GS-2</option>
            <option value="GS-3">GS-3</option>
            <option value="GS-4">GS-4</option>
            <option value="Ethics">Ethics</option>
            <option value="Extra">Extra</option>
          </select>
        </div>

        {/* Subject */}
        <div className="mb-4">
          <label className="block mb-1 font-medium text-gray-800 dark:text-gray-200">
            Subject
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
            className="w-full p-2 border rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
            placeholder="e.g., Geography, Polity, Disaster Management"
          />
        </div>

        {/* Draft / Publish */}
        <div className="mb-4 flex items-center gap-3">          
          <label className="font-medium text-gray-800 dark:text-gray-200">
            Status:
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="p-2 border rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
          >
            <option value="draft">Draft (Save for later)</option>
            <option value="published">Publish Now</option>
          </select>
        </div>

        {/* Free / Premium */}
<div className="mb-4 flex items-center gap-3">
  <label className="font-medium text-gray-800 dark:text-gray-200">
    Access:
  </label>

  <select
    value={isFree}
    onChange={(e) => setIsFree(e.target.value === "true")}
    className="p-2 border rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
  >
    <option value="true">Free</option>
    <option value="false">Premium</option>
  </select>
</div>

        {/* Questions */}
        {questions.map((q, idx) => (
          <div
            key={idx}
            className="mb-4 border p-4 rounded-lg bg-gray-50 dark:bg-gray-800 shadow-sm relative"
          >
            <label className="block mb-1 font-medium text-gray-900 dark:text-gray-100">
              Question {idx + 1}
            </label>
            <textarea
              rows={3}
              value={q.question}
              onChange={(e) =>
                handleQuestionChange(idx, "question", e.target.value)
              }
              className="w-full p-2 border rounded mb-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
              required
            />

            <label className="block mb-1 font-medium text-gray-900 dark:text-gray-100">
              Model Answer
            </label>
            <textarea
              rows={3}
              value={q.modelAnswer}
              onChange={(e) =>
                handleQuestionChange(idx, "modelAnswer", e.target.value)
              }
              className="w-full p-2 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
              required
            />

            {questions.length > 1 && (
              <button
                type="button"
                onClick={() => removeQuestion(idx)}
                className="absolute top-2 right-2 text-red-600 dark:text-red-400 font-bold"
                title="Remove question"
              >
                ✕
              </button>
            )}
          </div>
        ))}

        {/* Add Question */}
        <button
          type="button"
          onClick={addQuestion}
          className="mb-4 text-blue-600 dark:text-blue-400 underline"
        >
          + Add Question
        </button>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition"
        >
          {loading ? "Uploading..." : "Upload Test"}
        </button>
      </form>
    </div>
  );
};

export default MainsTestUpload;
